<template>
  <div class="user-details">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="userData.email"
        :pills="headerPills"
        :avatar="profilePicUrl"
        :avatar-fallback="headerInitials"
        sticky
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="32">{{ $t('common.cancel') }}</b-button>
          <b-button type="is-primary" @click="saveClicked" icon-left="content-save" tabindex="33">{{ $t('common.save') }}</b-button>
          <b-button
              v-if="!isLoading && !isUserArchived && !isCurrentUserSelected"
              type="is-danger"
              icon-left="archive-arrow-down"
              @click="archiveClicked"
          >{{ $t('user-details-view.archive-user-button') }}</b-button>
          <b-button
              v-else-if="!isLoading && isUserArchived && !isCurrentUserSelected"
              icon-left="archive-arrow-up-outline"
              @click="unArchiveClicked"
          >{{ $t('user-details-view.unarchive-user-button') }}</b-button>
        </template>
        <template v-else>
          <b-button @click="backClicked" icon-left="arrow-left">{{ $t('common.back') }}</b-button>
          <b-button type="is-primary" @click="editClicked" icon-left="pencil">{{ $t('common.edit') }}</b-button>
          <b-dropdown :triggers="['hover']" aria-role="list" position="is-bottom-left">
            <template #trigger>
              <b-button icon-left="dots-horizontal" />
            </template>
            <b-dropdown-item v-if="isPasswordResetVisible" aria-role="listitem" @click="passwordResetClicked">
              <b-icon icon="lock-reset" size="is-small" /> {{ $t('password-reset-view.send-password-reset-button') }}
            </b-dropdown-item>
            <hr class="dropdown-divider" v-if="!isCurrentUserSelected">
            <b-dropdown-item v-if="!isCurrentUserSelected" aria-role="listitem" @click="deleteClicked">
              <b-icon icon="delete" size="is-small" type="is-danger" /> {{ $t('common.delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </template>
    </OmxPageHeader>

    <OmxCard class="user-details__card">
      <div class="columns">
        <div class="column is-half">
          <OmxField
              v-model="userData.name.first"
              :label="$t('user-details-view.user-firstname-field')"
              :placeholder="$t('user-details-view.user-firstname-field')"
              :view-only="!isEditMode"
              rules="required"
              required
              tabindex="11"
          />
          <OmxField
              v-model="userData.name.last"
              :label="$t('user-details-view.user-lastname-field')"
              :placeholder="$t('user-details-view.user-lastname-field')"
              :view-only="!isEditMode"
              rules="required"
              required
              tabindex="12"
          />
          <OmxField
              v-if="isEditMode && !isCurrentUserSelected && hasCurrentUserAccessToUserRole(userData.roles[0])"
              v-model="userData.roles[0]"
              type="select"
              :label="$t('user-details-view.user-role-field')"
              :placeholder="$t('user-details-view.user-role-field')"
              :options="roleOptions"
              tabindex="13"
          />
          <OmxField
              v-else
              :label="$t('user-details-view.user-role-field')"
              view-only
          ><template #value>{{ userRole }}</template></OmxField>

          <OmxField
              :label="$t('user-details-view.user-email-field')"
              :value="userData.email"
              view-only
          />
          <OmxField
              v-model="userData.telephone"
              :label="$t('user-details-view.user-telephone-field')"
              :placeholder="$t('user-details-view.user-telephone-field')"
              :view-only="!isEditMode"
              autocomplete="tel"
              tabindex="15"
          />
          <OmxField
              v-model="userData.mobile"
              :label="$t('user-details-view.user-mobile-field')"
              :placeholder="$t('user-details-view.user-mobile-field')"
              :view-only="!isEditMode"
              autocomplete="tel"
              tabindex="16"
          />
        </div>

        <div class="column is-half">
          <div class="columns no-margin">
            <div class="column is-half">
              <OmxField
                  v-model="userData.address.street"
                  :label="$t('user-details-view.user-address-street-field')"
                  :placeholder="$t('user-details-view.user-address-street-field')"
                  :view-only="!isEditMode"
                  autocomplete="street-address"
                  tabindex="20"
              />
            </div>
            <div class="column is-half">
              <OmxField
                  v-model="userData.address.house_number"
                  :label="$t('user-details-view.user-address-house-number-field')"
                  :placeholder="$t('user-details-view.user-address-house-number-field')"
                  :view-only="!isEditMode"
                  tabindex="21"
              />
            </div>
          </div>
          <div class="columns no-margin">
            <div class="column is-half">
              <OmxField
                  v-model="userData.address.city"
                  :label="$t('user-details-view.user-address-city-field')"
                  :placeholder="$t('user-details-view.user-address-city-field')"
                  :view-only="!isEditMode"
                  autocomplete="address-level2"
                  tabindex="22"
              />
            </div>
            <div class="column is-half">
              <OmxField
                  v-model="userData.address.country"
                  :label="$t('user-details-view.user-address-country-field')"
                  :placeholder="$t('user-details-view.user-address-country-field')"
                  :view-only="!isEditMode"
                  autocomplete="country-name"
                  tabindex="23"
              />
            </div>
          </div>
          <div class="columns">
            <div class="column is-half">
              <OmxField
                  v-model="userData.address.postcode"
                  :label="$t('user-details-view.user-address-postcode-field')"
                  :placeholder="$t('user-details-view.user-address-postcode-field')"
                  :view-only="!isEditMode"
                  autocomplete="postal-code"
                  tabindex="24"
              />
            </div>
          </div>
          <ImagePicker
              v-if="isEditMode"
              :is-edit-mode="isEditMode"
              :image-url="profilePicUrl"
              @onImageFileRemoveSelected="onProfilePicFileRemoveSelected"
              @onImageFileSelected="onProfilePicFileSelected"
          />
        </div>
      </div>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import ImagePicker from "@/components/ImagePicker";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";

export default {
  name: "UserDetails",
  components: { ImagePicker, OmxPageHeader, OmxCard, OmxField },
  props: ['id'],
  data() {
    return {
      userData: {
        name: { first: "", last: "" },
        address: { street: "", house_number: "", city: "", country: "", postcode: "" }
      },
      isLoading: false,
      isEditMode: false,
      currentUserRole: null,
      profilePicFile: null,
      profilePicUrl: null
    }
  },
  watch: {
    id: function () {
      console.log("prop id changed.")
      this.readUserData();
      this.isEditMode = false;
    }
  },
  methods: {
    readUserData: function () {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUserFunction({action: "get_user_by_id", document_id: this.userId})
          .then((result) => {
            this.isLoading = false
            this.userData = result.data["users"];
          })
          .catch((error) => {
            this.isLoading = false;
            if (error.message === "InvalidPath") {
              this.$router.replace("../users");
            }
            this.showError(error.message)
            console.log("Error getting user: ", error?.code);
          });
    },
    updateUser: function (updateData, updateIsArchive) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "update_user";
      updateUserFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.user-updated'));
        if (updateIsArchive === true && result.data.payload !== undefined) {
          this.userData.is_archived = result.data.payload["users"].updated[0].is_archived["current"]
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error updating user: ", error?.code);
      });
      if (!updateIsArchive) {
        this.updateProfilePicture();
      }
    },
    downloadProfilePicUrl: function () {
      this.profilePicFile = null
      this.profilePicUrl = null
      this.getProfilePicRef().getDownloadURL().then((downloadURL) => {
        this.profilePicUrl = downloadURL
      }).catch((error) => {
        console.log('ProfilePic error: ', error?.code);
      });
    },
    updateProfilePicture: function () {
      if (this.profilePicFile !== null && this.profilePicFile !== undefined) {
        this.profilePicUrl = ""
        this.getProfilePicRef().put(this.profilePicFile).then(() => {
          this.downloadProfilePicUrl()
        }).catch((error) => {
          this.showError(error.message)
          console.log('ProfilePic upload error: ', error?.code);
          this.downloadProfilePicUrl()
        });
      } else {
        console.log("No profile pic is selected!")
      }
    },
    getProfilePicRef: function () {
      const imagePath = this.getStorageRootFolder() + '/users/' + this.userId + '/profilePic'
      return firebase.storage().ref(imagePath)
    },
    onProfilePicFileSelected: function (selectedImageFile) {
      this.profilePicFile = selectedImageFile
      this.profilePicUrl = URL.createObjectURL(this.profilePicFile)
    },
    onProfilePicFileRemoveSelected() {
      this.downloadProfilePicUrl()
    },
    archiveUser: function () {
      const dataFieldsToUpdate = {
        document_id: this.userId,
        is_archived: true
      }
      this.updateUser(dataFieldsToUpdate, true);
    },
    deleteUser: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteProjectFunction({
        action: "delete_user",
        document_id: this.id,
      }).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.user-deleted'));
        this.$emit('userDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result: ", error?.code);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    async deleteClicked() {
      if (!this.userData.is_archived) {
        await this.$omxAlert({
          title: this.$t('user-details-view.delete-confirm-title').toString(),
          message: this.$t('user-details-view.delete-unarchived-user-message').toString(),
          type: 'is-warning',
          icon: 'alert'
        });
        return;
      }
      const ok = await this.$omxConfirm({
        title: this.$t('user-details-view.delete-confirm-title').toString(),
        message: this.$t('user-details-view.delete-confirm-message').toString(),
        confirmText: this.$t('user-details-view.delete-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteUser();
    },
    cancelClicked: function () {
      if (this.isEditMode) {
        this.downloadProfilePicUrl()
        this.readUserData();
        this.isEditMode = !this.isEditMode;
      }
    },
    saveClicked: function () {
      // Required fields must be present before we attempt to save.
      const name = this.userData.name || {};
      const firstName = (name.first || "").toString().trim();
      const lastName = (name.last || "").toString().trim();
      if (!firstName || !lastName) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        document_id: this.userId,
        address: this.userData.address,
        name: this.userData.name,
        telephone: this.userData.telephone,
        mobile: this.userData.mobile,
        roles: this.userData.roles
      }
      this.updateUser(dataFieldsToUpdate, false);
    },
    async archiveClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('user-details-view.archive-user-confirm-title').toString(),
        message: this.$t('user-details-view.archive-user-confirm-message').toString(),
        confirmText: this.$t('user-details-view.archive-user-confirm-button').toString(),
        type: 'is-danger',
        icon: 'archive-arrow-down'
      });
      if (ok) this.archiveUser();
    },
    unArchiveClicked: function () {
      const dataFieldsToUpdate = {
        document_id: this.userId,
        is_archived: false
      }
      this.updateUser(dataFieldsToUpdate);
    },
    passwordResetClicked: function () {
      this.isLoading = true
      firebase
          .auth()
          .sendPasswordResetEmail(this.userData.email)
          .then(() => {
            this.isLoading = false
            this.showSuccess(this.$t("password-reset-view.password-reset-sent"))
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message);
          });
    },
    backClicked: function () {
      this.$router.back()
    }
  },
  mounted() {
    if (!this.currentUserAccess("UserDetails", this.UserActions.VIEW, null, this.id)) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readUserData();
    this.downloadProfilePicUrl();
    this.currentUserRole = this.getLocalUserRole();
  },
  computed: {
    pageTitle() {
      if (this.isEditMode) {
        return this.$t('user-details-view.edit-user-title')
      } else {
        return this.$t('user-details-view.view-user-title')
      }
    },
    userId() {
      return this.id
    },
    isUserArchived() {
      return this.userData.is_archived
    },
    isCurrentUserSelected() {
      return this.isCurrentUser(this.id);
    },
    isPasswordResetVisible() {
      return (this.currentUserRole !== null && this.currentUserRole !== undefined && this.currentUserAccess("UserDetails", this.UserActions.UPDATE, null, this.id))
          || this.isCurrentUserSelected
    },
    userRole() {
      if (this.userData.roles !== undefined && this.userData.roles.length > 0) {
        const st = this.userRoleOptions.find(obj => {
          return obj.id === this.userData.roles[0]
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return this.$t("enums.user-role.inactive");
    },
    headerTitle() {
      const first = this.userData.name && this.userData.name.first;
      const last = this.userData.name && this.userData.name.last;
      const full = [first, last].filter(Boolean).join(' ').trim();
      return full || this.userData.email || this.$t('user-details-view.view-user-title');
    },
    headerInitials() {
      const first = (this.userData.name && this.userData.name.first) || "";
      const last = (this.userData.name && this.userData.name.last) || "";
      const initials = (first.charAt(0) + last.charAt(0)).toUpperCase();
      if (initials) return initials;
      const email = this.userData.email || "";
      return email.slice(0, 2).toUpperCase() || "·";
    },
    roleOptions() {
      return this.getUserRoleList().map(opt => ({
        value: opt.id,
        label: this.$t(opt.translationKey)
      }));
    },
    headerPills() {
      const pills = [];
      if (this.userData.roles && this.userData.roles[0]) {
        pills.push({ label: this.userRole, tone: 'draft', dot: false });
      }
      if (this.isUserArchived) {
        pills.push({ label: this.$t('user-details-view.archive-user-button'), tone: 'warn' });
      }
      if (this.isCurrentUserSelected) {
        pills.push({ label: this.$t('top-menu.account'), tone: 'live', dot: false });
      }
      return pills;
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.user-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.user-details__card ::v-deep .columns:last-child { margin-bottom: 0; }
.no-margin {
  margin-bottom: 0 !important;
}
</style>
